import { UseMutationResult } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Icon, { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import { InboxEntryAlowClose, InboxEntryStatus } from '@/src/components/project/Inbox2/types';
import { RootState } from '@/src/reducers/types';

import StatusDropdown from '../../../Common/StatusDropdown/StatusDropdown';

export const getTooltip = (fileEntryId: number, isDisabled: boolean) => {
  if (!isDisabled) {
    return null;
  }

  const tooltipId = `tufs_${fileEntryId}`;

  return {
    id: tooltipId,
    component: (
      <Tooltip className="" id={tooltipId} place="bottom" effect="solid">
        <span>
          <Icon
            icon={ICONS.info}
            iconType={ICON_TYPES.outlined}
            className={clsx('tw-mr-[5px]', 'tw-text-white tw-text-[18px]')}
          />
          Requires action to close entry.
        </span>
      </Tooltip>
    ),
  };
};

export type FileEntryUpdateFileStatus = {
  id: number;
  status: InboxEntryStatus;
  allowedStatusChanges: InboxEntryStatus[];
  allowClose: InboxEntryAlowClose;
  duplicate: boolean;
};

const checkDisabled = ({
  fileEntry,
  projectRequired,
}: {
  fileEntry: FileEntryUpdateFileStatus;
  projectRequired: boolean;
}) => {
  if (!projectRequired) {
    return false;
  }

  if (fileEntry.status === InboxEntryStatus.Closed) {
    return false;
  }

  if (fileEntry.allowClose.allowed === false) {
    return true;
  }

  return fileEntry.allowedStatusChanges ? fileEntry.allowedStatusChanges.length < 1 : true;
};

type UpdateFileStatusProps = {
  fileEntry: FileEntryUpdateFileStatus;
  onStatusUpdated?: (response: any) => void;
  updateStatusMutation: UseMutationResult<any, any, { status: InboxEntryStatus }, any>;
};

const UpdateFileStatus = ({
  fileEntry,
  updateStatusMutation,
  onStatusUpdated,
}: UpdateFileStatusProps) => {
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const project = useSelector((store: RootState) => store.project.Selected);
  const isDisabled = checkDisabled({
    projectRequired: project.requiredAction,
    fileEntry,
  });
  const isReadOnly = loggedUser.data.userRole >= 5;
  const tooltip = !isReadOnly && getTooltip(fileEntry.id, isDisabled);

  const handleSelectOption = useCallback(
    async (newStatus) => {
      const response = await updateStatusMutation.mutateAsync({
        status: newStatus.value as InboxEntryStatus,
      });

      if (response && onStatusUpdated) {
        onStatusUpdated(response);
      }

      return response?.data;
    },
    [updateStatusMutation, onStatusUpdated]
  );

  if (!fileEntry.status) {
    return null;
  }

  return (
    <React.Fragment>
      {updateStatusMutation.isLoading && <Spinner />}
      <StatusDropdown
        value={fileEntry.status}
        isDisabled={isDisabled || isReadOnly}
        isDuplicate={fileEntry.duplicate}
        // onSelect={handleSelect}
        onSelectOption={handleSelectOption}
        tooltip={tooltip}
      />
    </React.Fragment>
  );
};

export default UpdateFileStatus;
